<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="anna-picker">
    <div class="anna-picker-head">
      <oxd-text tag="p" class="anna-picker-count">
        {{ countText }}
      </oxd-text>
      <oxd-button
        display-type="text"
        label="Refresh list"
        :disabled="busy"
        @click="load"
      />
    </div>

    <oxd-text v-if="error" tag="p" class="anna-picker-warn">
      {{ error }}
    </oxd-text>
    <oxd-text v-if="notVisible.length" tag="p" class="anna-picker-warn">
      Set but not visible to this key: {{ notVisible.join(', ') }}. The key only
      reaches its own Vapi organisation. Create the private key (and the public
      key) in the organisation that owns this assistant.
    </oxd-text>

    <oxd-grid :cols="2" class="orangehrm-full-width-grid">
      <oxd-grid-item v-for="slot in shownSlots" :key="slot.key">
        <search-select
          :label="`${slot.label} assistant`"
          :options="options"
          :model-value="current[slot.key]"
          :disabled="busy || !assistants"
          placeholder="Choose an assistant"
          @update:model-value="onSelect(slot, $event)"
        />
      </oxd-grid-item>
    </oxd-grid>
  </div>
</template>

<script>
import {APIService} from '@ohrm/core/util/services/api.service';
import SearchSelect from './SearchSelect.vue';

export default {
  name: 'AssistantPicker',
  components: {
    'search-select': SearchSelect,
  },
  props: {
    // one interviewer's picker only (general | technical); both when empty
    only: {
      type: String,
      default: '',
    },
    current: {
      type: Object,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['pick'],
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/anna/vapi/assistants',
    );
    // "no key yet" is shown in place, not as an error toast
    http.setIgnorePath('/api/v2/anna/vapi/assistants');
    return {http};
  },
  data() {
    return {
      assistants: null,
      error: null,
      slots: [
        {
          key: 'general',
          label: 'HR interview',
          setting: 'vapi_assistant_general',
        },
        {
          key: 'technical',
          label: 'Technical interview',
          setting: 'vapi_assistant_technical',
        },
      ],
    };
  },
  computed: {
    shownSlots() {
      return this.only
        ? this.slots.filter((s) => s.key === this.only)
        : this.slots;
    },
    options() {
      return (this.assistants || [])
        .map((a) => ({id: a.id, label: a.name || '(no name)'}))
        .sort((x, y) => x.label.localeCompare(y.label));
    },
    countText() {
      if (!this.assistants) return 'Vapi assistants';
      const n = this.assistants.length;
      return `${n} assistant${
        n === 1 ? '' : 's'
      } in this key's Vapi organisation`;
    },
    // configured ids the key cannot reach: the usual cause is a shared assistant
    notVisible() {
      if (!this.assistants) return [];
      const visible = new Set(this.assistants.map((a) => a.id));
      const byId = {};
      this.slots.forEach((slot) => {
        const id = this.current[slot.key];
        if (id && !visible.has(id)) {
          byId[id] = [...(byId[id] || []), slot.label];
        }
      });
      return Object.entries(byId).map(
        ([id, labels]) => `${id} (${labels.join(' and ')})`,
      );
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.error = null;
      this.http
        .getAll()
        .then((response) => {
          this.assistants = response.data.data;
        })
        .catch((error) => {
          this.assistants = null;
          this.error =
            error?.data?.error?.message || 'Could not load the list from Vapi.';
        });
    },
    onSelect(slot, id) {
      if (id && id !== this.current[slot.key]) {
        this.$emit('pick', slot.setting, id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.anna-picker {
  margin-top: 16px;
}
.anna-picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.anna-picker-count {
  font-size: 12px;
  font-weight: 700;
  color: #6a7482;
}
.anna-picker-warn {
  margin: 6px 0 10px;
  font-size: 13px;
  line-height: 1.5;
  color: #b8860b;
  background: #fdf1de;
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
